import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const SLOTS = [29, 8, 3, 1.5, 1, 0.5, 0.3, 0.5, 1, 1.5, 3, 8, 29];

export default function PlinkoGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [dropping, setDropping] = useState(false);
  const [chip, setChip] = useState(null); // {row, col}
  const [landed, setLanded] = useState(null);

  const drop = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setDropping(true);
    setLanded(null);
    try {
      const { data } = await api.post("/games/plinko/drop", { bet });
      // animate along path
      let col = 0;
      for (let i = 0; i < data.path.length; i++) {
        col += data.path[i];
        setChip({ row: i + 1, col });
        sfx("tick");
        await new Promise((r) => setTimeout(r, 130));
      }
      setLanded(data);
      setUser((u) => ({ ...u, coins: data.coins }));
      react({ outcome: data.payout > bet ? "win" : "loss", game: "plinko", big: data.multiplier >= 8 });
      if (data.payout > bet) toast.success(`${data.multiplier}x · +${data.payout.toLocaleString()} coins`);
      else if (data.payout > 0) toast(`${data.multiplier}x · ${data.payout.toLocaleString()} coins back`);
      else toast(`${data.multiplier}x`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDropping(false);
      setChip(null);
    }
  };

  return (
    <GameFrame title="Plinko" subtitle="Drop the chip — edges pay 29x"
      side={<BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={dropping} onPlay={drop} playLabel={dropping ? "Dropping…" : "DROP CHIP"} testid="plinko-drop" />}>
      <div className="glass rounded-3xl p-6 border border-amber-500/20 glow-coins">
        <div className="relative mx-auto" style={{ maxWidth: 460 }}>
          {/* pegs */}
          {Array.from({ length: 12 }).map((_, r) => (
            <div key={r} className="flex justify-center gap-4 mb-4" style={{ paddingLeft: 0 }}>
              {Array.from({ length: r + 2 }).map((_, c) => {
                const active = chip && chip.row === r + 1 && chip.col === c;
                return (
                  <div key={c} className="relative">
                    <div className="w-2.5 h-2.5 rounded-full bg-white/30" />
                    {active && (
                      <motion.div layoutId="chip" className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-gradient-to-br from-amber-300 to-pink-500 shadow-[0_0_12px_rgba(245,158,11,0.8)]" />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {/* slots */}
          <div className="flex justify-center gap-1 mt-2" data-testid="plinko-slots">
            {SLOTS.map((m, i) => (
              <div key={i} className={`flex-1 text-center py-2 rounded-md text-[10px] sm:text-xs font-mono font-bold ${landed && landed.slot === i ? "bg-emerald-500 text-black scale-110" : m >= 8 ? "bg-pink-500/30 text-pink-300" : m >= 1.5 ? "bg-amber-500/20 text-amber-300" : "bg-white/5 text-gray-400"} transition-all duration-200`}>
                {m}x
              </div>
            ))}
          </div>
          {landed && !dropping && (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="plinko-result"
              className={`text-center mt-5 px-6 py-3 rounded-xl font-semibold ${landed.payout > bet ? "bg-emerald-500/15 text-emerald-300" : landed.payout > 0 ? "bg-amber-500/10 text-amber-200" : "bg-white/5 text-gray-400"}`}>
              <span className="font-mono">{landed.multiplier}x</span>
              {landed.payout > bet ? ` · 🎉 You won ${landed.payout.toLocaleString()} coins!` : landed.payout > 0 ? ` · ${landed.payout.toLocaleString()} coins back` : " · No win this drop"}
            </motion.div>
          )}
        </div>
      </div>
    </GameFrame>
  );
}
